import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { Route, Switch, useParams, useUrl } from 'crossroad';
import { X, BookOpen, UsersRound, Library, Milestone, Compass, DraftingCompass, Scroll, Sprout, Brain } from 'lucide-react';
import { IconTrowel } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { useActivityLogger } from '@/lib/activity-logger';
import { CanonContext } from '../context/CanonContext';
import PassageArchaeologicalBackground from './passage-archaeological-background';
import PassageCulturalBackground from './passage-cultural-background';
import PassageTimeline from './timeline/PassageTimeline';
import PassageMap from './passage-map';
import PassageBookContext from './passage-book-context';
import PassageScripturalContext from './passage-scriptural-context';
import PassageLiteraryContext from './passage-literary-context';
import PassageEngagementMethods from './passage-engagement-methods';
import PassageInductiveStudy from './passage-inductive-study';
import PassageLeaderToolkit from './passage-leader-toolkit.tsx';
import { Analytics } from '@/lib/analytics';
import { ContextIntro, type ContextType } from './context-intro';
import { cn } from '@/lib/utils';
import { type TransitionStage } from '@/lib/hooks/use-geographical-transition';
import { usePanelSettled } from '@/context/PanelSettledContext';

interface Props {
    passageId: string;
    isTransitionAnimating?: boolean;
    isGeoAnimating?: boolean;
    geoStage?: TransitionStage;
    geoMapCenter?: { lat: number; lng: number } | null;
    setGeoMarkerPosition?: (pos: { x: number; y: number }) => void;
}

// Simple custom bottom sheet (no Vaul)
// - Opens when on a /context/... route
// - Default height ~60% of viewport
// - Draggable via the handle; content area scrolls normally
// - If dragged near the minimum height, it closes back to the passage
const DEFAULT_HEIGHT_RATIO = 0.75;
const MAX_HEIGHT_RATIO = 0.95;
const MIN_HEIGHT_PX = 120;
const CLOSE_THRESHOLD_EXTRA_PX = 120; // how close to min before we treat as close
const SNAP_HEIGHT_RATIOS = [0.3, 0.45, 0.6, 0.75, 0.95]; // Soft snap heights as fractions of viewport height

const contextTitles: Record<ContextType, string> = {
    archaeology: 'Archaeological Context',
    canon: 'Outline',
    culture: 'Cultural Context',
    historicalEvents: 'Timeline',
    places: 'Map',
    book: 'Book Context',
    scripture: 'Scriptural Context',
    literary: 'Literary Context',
    engagementMethods: 'Ideas for Engagement',
    inductive: 'Inductive Study',
    leaderToolkit: 'Leader\'s Toolkit',
};

const contextIcons: Record<ContextType, React.ComponentType<{ className?: string }>> = {
    archaeology: IconTrowel,
    canon: Library,
    culture: UsersRound,
    historicalEvents: Milestone,
    places: Compass,
    book: BookOpen,
    scripture: Library,
    literary: DraftingCompass,
    engagementMethods: Sprout,
    inductive: Brain,
    leaderToolkit: UsersRound,
};

// Views that will use the new cinematic hero header
const CINEMATIC_VIEWS: Set<ContextType> = new Set([
    'engagementMethods',
    'inductive',
    'culture',
    'archaeology',
    'literary',
    'book',
    'scripture',
    'historicalEvents',
    'places',
    'leaderToolkit'
]);

export function PassageContextPanel({
    passageId,
    isTransitionAnimating,
    isGeoAnimating,
    geoStage,
    geoMapCenter,
    setGeoMarkerPosition
}: Props) {
    const isDarkMode = useDarkMode();
    const activityLogger = useActivityLogger();
    const { markSettled, resetSettled } = usePanelSettled();
    const contextCategory = useParams('/app/passage/:id/context/:contextCategory')?.contextCategory as ContextType | undefined;
    const isOpen = !!contextCategory;
    const [, setUrl] = useUrl();
    const [viewportHeight, setViewportHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 800);

    // Track panel open
    useEffect(() => {
        if (isOpen && contextCategory) {
            Analytics.contextPanelOpen(contextCategory as any);
            activityLogger.log('context_view', `Viewed ${contextTitles[contextCategory] || contextCategory}`, {
                contextType: contextCategory,
                passageId
            });
        }
    }, [isOpen, contextCategory, activityLogger, passageId]);

    // Update viewport height but with debounce/protection against tiny fluctuations
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleResize = () => {
            const newHeight = window.innerHeight;
            // Only update if difference is significant (e.g. more than 60px - typical mobile bar)
            // unless the panel is currently closed, then update freely
            // This prevents "sliding" in book context sticky sidebar
            setViewportHeight(prev => {
                if (!isOpen) return newHeight;
                // If it's a small change (address bar), don't update while open
                if (Math.abs(prev - newHeight) < 100) return prev;
                return newHeight;
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isOpen]);

    const closePanel = useCallback(() => {
        setUrl(`/app/passage/${passageId}`);
    }, [setUrl, passageId]);


    const [height, setHeight] = useState<number | null>(null);
    const [heightRatio, setHeightRatio] = useState(DEFAULT_HEIGHT_RATIO);
    const [isDragging, setIsDragging] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const dragStateRef = useRef<{ startY: number; startHeight: number; currentHeight: number } | null>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const hasNotifiedReadyRef = useRef(false);
    const title = contextCategory ? contextTitles[contextCategory] : '';
    const Icon = contextCategory ? contextIcons[contextCategory] : null;

    // Track the last height ratio to maintain it across resizes (e.g. rotation)
    // We update this whenever height changes but not while dragging
    useEffect(() => {
        if (height && viewportHeight > 0 && !isDragging) {
            setHeightRatio(height / viewportHeight);
        }
    }, [height, viewportHeight, isDragging]);

    // Handle viewport height changes (e.g. rotation) while the panel is open
    // This ensures the panel stays at the same relative height and doesn't get "stuck" off-screen
    useEffect(() => {
        if (isOpen && height !== null && viewportHeight > 0) {
            const targetHeight = Math.round(viewportHeight * heightRatio);
            const cappedHeight = Math.max(
                MIN_HEIGHT_PX,
                Math.min(targetHeight, Math.round(viewportHeight * MAX_HEIGHT_RATIO))
            );

            if (Math.abs(cappedHeight - height) > 1) {
                setHeight(cappedHeight);
            }
        }
    }, [viewportHeight, isOpen]);

    // Initialize height when panel opens
    useEffect(() => {
        if (!isOpen) {
            setHeight(null);
            setIsDragging(false);
            dragStateRef.current = null;
            hasNotifiedReadyRef.current = false;
            resetSettled(); // Reset for next open
            return;
        }

        if (height == null) {
            const vh = window.innerHeight || 0;
            setViewportHeight(vh);
            const initial = Math.max(
                MIN_HEIGHT_PX,
                Math.round(vh * DEFAULT_HEIGHT_RATIO)
            );
            setHeight(initial);
        }
    }, [isOpen, height]);


    // Expose current panel height as a CSS variable so other components
    // can adjust their layout (e.g. PassageDetails bottom padding)
    useEffect(() => {
        if (typeof document === 'undefined') return;

        const root = document.documentElement;

        if (!isOpen || height == null) {
            root.style.setProperty('--context-panel-height', '0px');
            return;
        }

        root.style.setProperty('--context-panel-height', `${height}px`);

        return () => {
            root.style.setProperty('--context-panel-height', '0px');
        };
    }, [isOpen, height, isDragging]);

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!isOpen) return;
        if (typeof window === 'undefined') return;

        const viewportHeight = window.innerHeight || 0;
        const currentHeight =
            height ?? Math.max(MIN_HEIGHT_PX, Math.round(viewportHeight * DEFAULT_HEIGHT_RATIO));

        // Capture pointer so we keep getting move events even if the user leaves the handle
        (event.target as HTMLElement).setPointerCapture(event.pointerId);
        dragStateRef.current = {
            startY: event.clientY,
            startHeight: currentHeight,
            currentHeight: currentHeight,
        };
        setIsDragging(true);
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!isDragging || !dragStateRef.current || !panelRef.current) return;
        if (typeof window === 'undefined') return;

        const vh = viewportHeight || window.innerHeight || 0;
        const maxHeight = Math.round(vh * MAX_HEIGHT_RATIO);
        const minHeight = MIN_HEIGHT_PX;

        const { startY, startHeight } = dragStateRef.current;
        const deltaY = startY - event.clientY; // dragging up -> increase height
        const nextHeight = Math.max(minHeight, Math.min(maxHeight, startHeight + deltaY));

        // Update DOM directly without React state to avoid re-renders
        panelRef.current.style.height = `${nextHeight}px`;
        dragStateRef.current.currentHeight = nextHeight;
    };

    const endDrag = () => {
        if (!isDragging) return;
        setIsDragging(false);

        // Sync the final height from ref to React state
        const finalHeight = dragStateRef.current?.currentHeight;
        if (finalHeight == null) return;

        // Close if dragged near the minimum height
        if (finalHeight <= MIN_HEIGHT_PX + CLOSE_THRESHOLD_EXTRA_PX) {
            closePanel();
            return;
        }

        // Soft snap to nearest preferred height
        const vh = viewportHeight || window.innerHeight || 0;
        const maxHeight = Math.round(vh * MAX_HEIGHT_RATIO);
        const minHeight = MIN_HEIGHT_PX;

        const snapHeights = SNAP_HEIGHT_RATIOS.map((ratio) =>
            Math.max(minHeight, Math.min(maxHeight, Math.round(vh * ratio)))
        );

        let closest = snapHeights[0];
        let smallestDiff = Math.abs(finalHeight - closest);

        for (let i = 1; i < snapHeights.length; i++) {
            const diff = Math.abs(finalHeight - snapHeights[i]);
            if (diff < smallestDiff) {
                smallestDiff = diff;
                closest = snapHeights[i];
            }
        }

        // Now sync to React state (only triggers one re-render at drag end)
        setHeight(closest);
    };

    const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        (event.target as HTMLElement).releasePointerCapture(event.pointerId);
        endDrag();
    };

    const handlePointerCancel = () => {
        endDrag();
    };

    if (!isOpen || height == null) {
        return null;
    }

    // Determine z-index
    const zIndex = 115; // Set slightly lower than footer (120) but higher than hero image (11)

    const isCinematic = contextCategory && CINEMATIC_VIEWS.has(contextCategory);

    return (
        <motion.div
            ref={panelRef}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
                duration: 0.25,
                ease: [0.22, 1, 0.36, 1],
                delay: isGeoAnimating ? 0.1 : 0
            }}
            onAnimationComplete={() => {
                // Mark panel as settled when entrance animation completes
                // (This fires for both entrance and exit, but resetSettled is called on close anyway)
                markSettled();
            }}
            className={cn(
                "fixed bottom-0 left-0 right-0 flex flex-col overflow-hidden",
                "bg-background/60 backdrop-blur-3xl border-t border-primary/20",
                "shadow-[0_-15px_30px_-5px_rgba(0,0,0,0.4),_0_-8px_15px_-8px_rgba(0,0,0,0.3)] rounded-t-[2.5rem]",
                isCinematic && "bg-background/40" // More transparent for cinematic background matching
            )}
            style={{
                height: height ?? 'auto',
                zIndex,
                // Keep it on the GPU
                transform: 'translateZ(0)',
                contain: 'layout style' // Isolate layout calculations to prevent child re-renders from affecting parent
            }}
        >
            {/* 1. Global Sticky Frame (Handle & Close Button) */}
            <div className="absolute top-0 left-0 right-0 z-[1000]">
                {/* Drag handle */}
                <div
                    className="flex justify-center items-center pt-3 pb-2 cursor-grab active:cursor-grabbing"
                    style={{ touchAction: 'none' }}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerCancel}
                >
                    <div className={cn(
                        "w-16 h-2 rounded-full transition-all duration-300 shadow-sm",
                        isCinematic
                            ? isDarkMode
                                ? "bg-white/40 hover:bg-white/80 backdrop-blur-md"
                                : (contextCategory === 'places' || isScrolled)
                                    ? "bg-black/20 hover:bg-black/30"
                                    : "bg-white/60 hover:bg-white/90 backdrop-blur-md"
                            : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                    )} />
                </div>

                {/* Floating Close Button for Cinematic Views */}
                {isCinematic && (
                    <div className="absolute top-4 right-4 pointer-events-auto">
                        <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                                "h-10 w-10 rounded-full backdrop-blur-md border transition-all duration-300 shadow-lg",
                                isDarkMode
                                    ? (contextCategory === 'places'
                                        ? "bg-black/70 border-white/20 hover:bg-black/80 text-white shadow-black/40"
                                        : "bg-white/20 border-white/30 hover:bg-white/40 text-white shadow-black/40")
                                    : (contextCategory === 'places' || isScrolled)
                                        ? "bg-white/90 border-black/15 hover:bg-white text-slate-900 shadow-black/10"
                                        : "bg-white/20 border-white/40 hover:bg-white/40 text-white shadow-black/20"
                            )}
                            onClick={closePanel}
                        >
                            <X className="h-5 w-5" />
                        </Button>
                    </div>
                )}
            </div>

            {/* 2. Standard Header Section (Title Bar) */}
            {
                !isCinematic && (
                    <div className="flex flex-col flex-shrink-0 touch-none select-none pt-8">
                        {/* Content Header Area */}
                        <div className="flex items-center justify-between px-6 pb-4">
                            <div className="flex items-center gap-3.5">
                                {Icon && <Icon className="h-6 w-6 text-primary" />}
                                <h2 className="text-xl font-bold tracking-tight text-foreground/90">{title}</h2>
                            </div>

                            <div className="flex items-center gap-2">
                                {/* Why button */}
                                {contextCategory && <ContextIntro type={contextCategory} inline />}

                                <div className="w-10 h-10" /> {/* Spacer for the global sticky close button which sits on top */}
                                <div className="absolute top-4 right-4 pointer-events-auto">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-10 w-10 rounded-full bg-muted/50 hover:bg-muted border border-border/70 hover:border-border transition-all shadow-sm"
                                        onClick={closePanel}
                                        title="Close"
                                    >
                                        <X className="h-5 w-5 text-muted-foreground" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className="h-px w-full bg-gradient-to-r from-transparent via-border/50 to-transparent" />
                    </div>
                )
            }

            {/* 3. Scrollable Content Area */}
            <div
                ref={contentRef}
                className={cn(
                    "flex-1 min-h-0 overflow-y-auto pb-24",
                    !isCinematic && "px-4 mt-2"
                )}
                onScroll={(e) => {
                    const target = e.currentTarget;
                    setIsScrolled(target.scrollTop > 80);
                }}
                style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}
            >
                <Switch>
                    <Route path="/app/passage/:passageId/context/canon">
                        <CanonContext passageId={passageId} />
                    </Route>
                    <Route path="/app/passage/:passageId/context/archaeology">
                        <PassageArchaeologicalBackground passageId={passageId} />
                    </Route>
                    <Route path="/app/passage/:passageId/context/culture">
                        <PassageCulturalBackground passageId={passageId} />
                    </Route>
                    <Route path="/app/passage/:passageId/context/historicalEvents">
                        <PassageTimeline
                            passageId={passageId}
                            isTransitionAnimating={isTransitionAnimating}
                        />
                    </Route>
                    <Route path="/app/passage/:passageId/context/places">
                        <PassageMap
                            passageId={passageId}
                            isGeoAnimating={isGeoAnimating}
                            geoStage={geoStage}
                            geoMapCenter={geoMapCenter}
                            setGeoMarkerPosition={setGeoMarkerPosition}
                        />
                    </Route>
                    <Route path="/app/passage/:passageId/context/book">
                        <PassageBookContext
                            passageId={passageId}
                            isTransitionAnimating={isTransitionAnimating}
                        />
                    </Route>
                    <Route path="/app/passage/:passageId/context/scripture">
                        <PassageScripturalContext
                            passageId={passageId}
                            isTransitionAnimating={isTransitionAnimating}
                        />
                    </Route>
                    <Route path="/app/passage/:passageId/context/literary">
                        <PassageLiteraryContext passageId={passageId} />
                    </Route>
                    <Route path="/app/passage/:passageId/context/engagementMethods">
                        <PassageEngagementMethods passageId={passageId} />
                    </Route>
                    <Route path="/app/passage/:passageId/context/inductive">
                        <PassageInductiveStudy passageId={passageId} />
                    </Route>
                    <Route path="/app/passage/:passageId/context/leaderToolkit">
                        <PassageLeaderToolkit passageId={passageId} />
                    </Route>
                </Switch>
            </div>
        </motion.div >
    );
}
